import { Check, GripVertical, X } from "lucide-react";

const human = (k) => k.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());

function toLines(value, depth = 0, out = []) {
  if (value == null) return out;
  if (typeof value !== "object") {
    out.push({ depth, text: String(value) });
  } else if (Array.isArray(value)) {
    value.forEach((v) => (typeof v === "object" && v ? toLines(v, depth + 1, out) : out.push({ depth, text: `• ${v}` })));
  } else {
    Object.entries(value).forEach(([k, v]) => {
      out.push({ depth, label: human(k) });
      toLines(v, depth + 1, out);
    });
  }
  return out;
}

export const AnswerView = ({ value }) => (
  <div className="space-y-1">
    {toLines(value).map((l, i) => (
      <div key={i} style={{ paddingLeft: l.depth * 12 }} className={l.label ? "pt-1 font-mono text-[11px] uppercase tracking-wider text-muted-foreground" : "leading-relaxed"}>{l.label || l.text}</div>
    ))}
  </div>
);

const Chip = ({ on, onClick, children, testId, order }) => (
  <button type="button" onClick={onClick} data-testid={testId} aria-pressed={on} className={`flex items-start gap-2 border p-2.5 text-left text-sm transition-colors ${on ? "border-brand-primary bg-brand-primary/10" : "border-border bg-card hover:bg-muted/60"}`}>
    <span className={`mt-0.5 flex h-5 min-w-[20px] items-center justify-center border text-[11px] font-mono ${on ? "border-brand-primary bg-brand-primary text-brand-primary-fg" : "border-border"}`}>{on ? (order ?? <Check className="h-3 w-3" />) : ""}</span>
    <span>{children}</span>
  </button>
);

const OrderGroup = ({ g, selected, onChange, testId }) => {
  const seq = selected.filter((s) => g.options.includes(s));
  const toggle = (o) => onChange(seq.includes(o) ? selected.filter((s) => s !== o) : [...selected, o]);
  return (
    <div className="space-y-3">
      <div className="grid gap-2 sm:grid-cols-2">
        {g.options.map((o, i) => <Chip key={o} on={seq.includes(o)} order={seq.indexOf(o) + 1} onClick={() => toggle(o)} testId={`${testId}-opt-${i}`}>{o}</Chip>)}
      </div>
      {seq.length > 0 && (
        <ol data-testid={`${testId}-sequence`} className="border border-dashed border-brand-primary/60 p-3 text-sm">
          {seq.map((s, i) => <li key={s} className="flex items-center gap-2 py-0.5"><GripVertical className="h-3.5 w-3.5 text-muted-foreground" /><span className="font-mono text-xs text-brand-primary">{i + 1}</span>{s}<button type="button" aria-label="Remove block" onClick={() => toggle(s)} className="ml-auto"><X className="h-3.5 w-3.5" /></button></li>)}
        </ol>
      )}
    </div>
  );
};

const SlotGroup = ({ g, selected, onChange, testId }) => {
  const cur = selected.find((s) => s.startsWith(`${g.label}=`))?.split("=")[1];
  const pick = (v) => onChange([...selected.filter((s) => !s.startsWith(`${g.label}=`)), `${g.label}=${v}`]);
  return (
    <div className="flex flex-wrap items-center gap-2 text-sm">
      <span className="min-w-[180px] font-semibold">{g.label}</span>
      {g.options.map((o) => <button key={o} type="button" data-testid={`${testId}-${o}`} onClick={() => pick(o)} className={`border px-2.5 py-1 font-mono text-xs ${cur === o ? "border-brand-primary bg-brand-primary text-brand-primary-fg" : "border-border"}`}>{o}</button>)}
    </div>
  );
};

export const TaskGroup = ({ g, selected, onChange, testId }) => {
  if (g.type === "info") return <p className="text-sm text-muted-foreground">{g.options[0]}</p>;
  if (g.type === "slot") return <SlotGroup g={g} selected={selected} onChange={onChange} testId={testId} />;
  return (
    <div>
      {g.label !== "options" && <div className="mb-2 font-mono text-[11px] uppercase tracking-wider text-muted-foreground">{g.label}{g.type === "order" && " · click in order to build the sequence"}</div>}
      {g.type === "order" ? <OrderGroup g={g} selected={selected} onChange={onChange} testId={testId} /> : (
        <div className="grid gap-2 sm:grid-cols-2">
          {g.options.map((o, i) => <Chip key={o} on={selected.includes(o)} onClick={() => onChange(selected.includes(o) ? selected.filter((s) => s !== o) : [...selected, o])} testId={`${testId}-opt-${i}`}>{o}</Chip>)}
        </div>
      )}
    </div>
  );
};
